/**
 * IndexedDB Offline Store — FIDSNet Display Client
 * ─────────────────────────────────────────────────────────────────────────────
 * Persists flight data, layout info, and schedules in the browser.
 * Survives page refresh + server outage (up to TTL).
 */

const DB_NAME = "fidsnet-display";
const DB_VERSION = 1;

const STORES = {
  FLIGHTS: "flights",
  LAYOUTS: "layouts",
  SCHEDULES: "schedules",
  CONFIG: "config",
};

const TTL_MS = 4 * 60 * 60 * 1000; // 4 hours (airport requirement: display last-known data during outages)

let dbInstance = null;

// ─── Open/Create Database ───────────────────────────────────────────────────
const openDB = () => {
  if (dbInstance) return Promise.resolve(dbInstance);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      if (!db.objectStoreNames.contains(STORES.FLIGHTS)) {
        db.createObjectStore(STORES.FLIGHTS, { keyPath: "direction" });
      }
      if (!db.objectStoreNames.contains(STORES.LAYOUTS)) {
        db.createObjectStore(STORES.LAYOUTS, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORES.SCHEDULES)) {
        db.createObjectStore(STORES.SCHEDULES, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(STORES.CONFIG)) {
        db.createObjectStore(STORES.CONFIG, { keyPath: "key" });
      }
    };

    request.onsuccess = (event) => {
      dbInstance = event.target.result;
      resolve(dbInstance);
    };

    request.onerror = (event) => {
      console.error("[OfflineStore] Failed to open IndexedDB:", event.target.error);
      reject(event.target.error);
    };
  });
};

// ─── Generic Operations ─────────────────────────────────────────────────────

const put = async (storeName, data) => {
  try {
    const db = await openDB();
    const tx = db.transaction(storeName, "readwrite");
    const store = tx.objectStore(storeName);
    store.put({ ...data, _savedAt: Date.now() });
    return new Promise((resolve, reject) => {
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
  } catch (err) {
    console.warn("[OfflineStore] put failed:", err.message);
  }
};

const get = async (storeName, key) => {
  try {
    const db = await openDB();
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    const request = store.get(key);
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const result = request.result;
        // Check TTL
        if (result && result._savedAt && Date.now() - result._savedAt > TTL_MS) {
          resolve(null); // Expired
        } else {
          resolve(result || null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.warn("[OfflineStore] get failed:", err.message);
    return null;
  }
};

const getAll = async (storeName) => {
  try {
    const db = await openDB();
    const tx = db.transaction(storeName, "readonly");
    const store = tx.objectStore(storeName);
    const request = store.getAll();
    return new Promise((resolve, reject) => {
      request.onsuccess = () => {
        const now = Date.now();
        const results = (request.result || []).filter(
          (item) => !item._savedAt || now - item._savedAt <= TTL_MS,
        );
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (err) {
    console.warn("[OfflineStore] getAll failed:", err.message);
    return [];
  }
};

// ─── Flight Data ────────────────────────────────────────────────────────────

export const saveFlightData = async (direction, data) => {
  await put(STORES.FLIGHTS, { direction, data });
};

export const getFlightData = async (direction) => {
  const record = await get(STORES.FLIGHTS, direction);
  return record?.data || null;
};

// ─── Layout URL ─────────────────────────────────────────────────────────────

export const saveCurrentLayout = async (url, deviceId) => {
  await put(STORES.LAYOUTS, { id: "current", url, deviceId });
};

export const getCurrentLayout = async () => {
  const record = await get(STORES.LAYOUTS, "current");
  return record?.url || null;
};

// ─── Schedules ──────────────────────────────────────────────────────────────

export const saveSchedules = async (schedules) => {
  await put(STORES.SCHEDULES, { id: "active", schedules });
};

export const getSchedules = async () => {
  const record = await get(STORES.SCHEDULES, "active");
  return record?.schedules || [];
};

// ─── Config (pairing info) ──────────────────────────────────────────────────

export const saveConfig = async (key, value) => {
  await put(STORES.CONFIG, { key, value });
};

export const getConfig = async (key) => {
  const record = await get(STORES.CONFIG, key);
  return record?.value || null;
};

export default {
  saveFlightData,
  getFlightData,
  saveCurrentLayout,
  getCurrentLayout,
  saveSchedules,
  getSchedules,
  saveConfig,
  getConfig,
};
